/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { SetterOrUpdater, useRecoilValue } from "recoil";
import { ContactApi, IUpdateContactResponse } from "../../../../api/Contacts";
import { getAllAppsTags } from "../../../../api/Learnistic";
import BerserkerLogo from "../../../../assets/images/berserker-logo";
import LearnisticLogo from "../../../../assets/images/learnistic-logo";
import { ContactFilter } from "../../../../atoms/ContactFilterAtom";
import { IContactFiltersAtom } from "../../../../atoms/ContactFiltersAtom";
import {
  IContactTag,
  ILearnisticApp,
  ILearnisticTag,
} from "../../../../generated/Api";
import { IContact } from "../../../../types/Contact";
import { ITag } from "../../../../types/TagTypes";
import { LearnistiTagsToApps } from "../../../../utils/LearnisticTagConvertions";
import LearnisticTagSelect from "../../../LearnisticTagSelect/LearnisticTagSelect";
import LoadingAbsolute from "../../../LoadingAbsolute";
import { AllIntegrationsAtom } from "../../../Settings/Integrations/Integrations";
import StyledButton from "../../../StyledButton";
import TagSelect from "../../../TagSelect/TagSelect";
import { useModal } from "../../Modal";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  gap: 20px;
  margin-top: 20px;
`;

const Row = styled.div`
  display: flex;

  width: 100%;
  gap: 32px;
`;

const Logo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  width: 100%;

  > span {
    font-size: 14px;
    color: #707070;
    text-align: center;
  }
`;

const TagContainer = styled.div`
  display: flex;
  flex-direction: column;

  gap: 4px;

  > span {
    font-size: 16px;
    font-weight: 600;
    color: #2f2f2f;
  }

  width: 100%;

  > div {
    width: 350px;
  }
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;

  width: calc(100% + 50px);
  gap: 12px;

  padding: 20px 20px 0 20px;

  border-top: 1px solid #dddddd;
`;

type Props = {
  setContacts?: SetterOrUpdater<IContact[]>;
} & (
  | {
      selectedContactIds: string[];
    }
  | {
      selectAll: true;
      contactFilter?: ContactFilter | null;
      excludeContactIds?: string[];
      query: IContactFiltersAtom;
    }
);

export default function BulkAddAndRemoveTagModal(props: Props): JSX.Element {
  const modal = useModal();

  const [isLoading, setIsLoading] = useState(false);
  const [addSelectedTags, setAddSelectedTags] = useState<ITag[]>([]);
  const [removeSelectedTags, setRemoveSelectedTags] = useState<ITag[]>([]);
  const integrations = useRecoilValue(AllIntegrationsAtom);
  const [learnisticApps, setLearnisticApps] = useState<ILearnisticApp[] | null>(
    []
  );

  const [addSelectedLearnisticTags, setAddSelectedLearnisticTags] = useState<
    ILearnisticTag[]
  >([]);
  const [removeSelectedLearnisticTags, setRemoveSelectedLearnisticTags] =
    useState<ILearnisticTag[]>([]);

  const hasLearnisticIntegration = integrations.some(
    (i) => i.type === "LEARNISTIC"
  );

  useEffect(() => {
    if (!hasLearnisticIntegration) return;

    getAllAppsTags().then((tags) => {
      setLearnisticApps(tags);
    });
  }, []);

  async function handleClick() {
    const mappedAddTags = LearnistiTagsToApps(
      learnisticApps,
      addSelectedLearnisticTags
    );

    const mappedRemoveTags = LearnistiTagsToApps(
      learnisticApps,
      removeSelectedLearnisticTags
    );

    try {
      setIsLoading(true);

      const addTagIds = addSelectedTags.map(({ _id }) => _id!);
      const removeTagIds = removeSelectedTags.map(({ _id }) => _id!);

      let response: IUpdateContactResponse;

      if ("selectAll" in props) {
        let tagQuery = props.contactFilter?.tagQuery;
        response = await ContactApi.addAndRemoveManyTag({
          addTagIds: addTagIds,
          removeTagIds: removeTagIds,
          addLearnisticTags: mappedAddTags,
          removeLearnisticTags: mappedRemoveTags,
          query: {
            ...props.query,
            tagQuery,
          },
          excludeContactIds: props.excludeContactIds,
        });
      } else {
        response = await ContactApi.addAndRemoveManyTag({
          addTagIds: addTagIds,
          removeTagIds: removeTagIds,
          contactIds: props.selectedContactIds,
          addLearnisticTags: mappedAddTags,
          removeLearnisticTags: mappedRemoveTags,
        });
      }

      if (!response) {
        toast("Error on add and remove tags on contacts, try again.", {
          type: "error",
        });
        return;
      }

      if (addTagIds.length > 0 && !removeTagIds.length) {
        toast("Tags added successfully!", { type: "success" });
      } else if (removeTagIds.length > 0 && !addTagIds.length) {
        toast("Tags removed successfully!", { type: "success" });
      } else {
        toast("Tags added/removed successfully!", { type: "success" });
      }

      if (!props.setContacts) return;

      props.setContacts((prevContacts) => {
        if (!prevContacts) return;

        const array = prevContacts.map((prevContact, index) => {
          if (
            "selectedContactIds" in props &&
            !props.selectedContactIds.includes(prevContact._id)
          )
            return prevContact;

          if (
            "selectAll" in props &&
            props.excludeContactIds?.includes(prevContact._id)
          )
            return prevContact;

          let allContactTags = prevContact.tags.filter(
            (tag) => !removeTagIds.find((tagId) => tag._id === tagId)
          );

          allContactTags = [
            ...allContactTags,
            ...addSelectedTags
              .filter((at) => !allContactTags.find((t) => t._id === at._id))
              .map((t) => ({
                _id: t._id as string,
                appliedAt: new Date().toDateString(),
              })),
          ];

          let uniqueTagsMap: { [tagId: string]: IContactTag } = {};

          allContactTags.forEach((t) => (uniqueTagsMap[t._id] = t));

          return {
            ...prevContact,
            tags: Object.values(uniqueTagsMap),
          };
        });

        return array;
      });
    } catch (err) {
      console.log({ err });
    } finally {
      setIsLoading(false);
      modal.popModal();
    }
  }

  return (
    <Container>
      {hasLearnisticIntegration ? (
        <Row>
          <Logo>
            <BerserkerLogo width={40} height={30} />
            <span>Manage your BerserkerMail tags</span>
          </Logo>
          <Logo>
            <LearnisticLogo width={40} height={30} />
            <span>Manage your Learnistic tags</span>
          </Logo>
        </Row>
      ) : (
        <div />
      )}
      <Row>
        <TagContainer>
          <span>Apply Tags</span>
          <TagSelect
            tags={addSelectedTags}
            preserveSystemTags={true}
            handleChoseTags={(tags) => setAddSelectedTags(tags)}
          />
        </TagContainer>

        {hasLearnisticIntegration && (
          <TagContainer>
            <span>Apply Tags</span>
            <LearnisticTagSelect
              tags={addSelectedLearnisticTags}
              tagsPool={learnisticApps}
              handleChoseTags={(tags) => setAddSelectedLearnisticTags(tags)}
            />
          </TagContainer>
        )}
      </Row>
      <Row>
        <TagContainer>
          <span>Remove Tags</span>
          <TagSelect
            tags={removeSelectedTags}
            preserveSystemTags={true}
            handleChoseTags={(tags) => setRemoveSelectedTags(tags)}
          />
        </TagContainer>
        {hasLearnisticIntegration && (
          <TagContainer>
            <span>Remove Tags</span>
            <LearnisticTagSelect
              tags={removeSelectedLearnisticTags}
              tagsPool={learnisticApps}
              handleChoseTags={(tags) => setRemoveSelectedLearnisticTags(tags)}
            />
          </TagContainer>
        )}
      </Row>

      <Footer>
        <StyledButton
          inverted
          css={css`
            width: 70px;
          `}
          onClick={modal.popModal}
        >
          Cancel
        </StyledButton>
        <StyledButton
          css={css`
            width: 70px;
          `}
          onClick={handleClick}
        >
          Save
        </StyledButton>
      </Footer>
      {isLoading && <LoadingAbsolute />}
    </Container>
  );
}
